import { Stack } from '@startuptools/ui/Stack';
import { Trans } from '@lingui/react';
import { useState } from 'react';
import { AgendaTable } from '../meetings/components/edit-steps/AgendaTable';
import {
  useMatDeleteMutation,
  useMatiDeleteMutation,
  useMatiSetOrderMutation,
  useMatSetNameMutation,
} from './BoardMeetingTemplateDetails.graphql';
import { BoardMeetingTemplatesDocument } from './BoardMeetingTemplates.graphql';
import { initial, orderBy, range } from 'lodash-es';
import { GqlMeetingAgendaTemplate, GqlMeetingAgendaTemplateItem } from '../../graphql/base-types.graphql';
import { Button } from '@startuptools/ui/Button';
import { IconButton } from '@startuptools/ui/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { PromptDialog } from '../../components/simple-dialogs/PromptDialog';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Input } from '@startuptools/ui/Input';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useMatSetIsDefaultMutation } from '../../graphql/react-operations';
import { BoardMeetingTemplateItemDialog } from './BoardMeetingTemplateItemDialog';

type FormValues = {
  name: string;
};

export const BoardMeetingTemplateDetails = ({
  mat,
}: {
  mat: Pick<GqlMeetingAgendaTemplate, 'id' | 'companyId' | 'isDefault' | 'name'> & {
    meetingAgendaTemplateItems: Pick<
      GqlMeetingAgendaTemplateItem,
      | 'id'
      | 'kind'
      | 'firstGroup'
      | 'lastGroup'
      | 'order'
      | 'subjectPrimaryLang'
      | 'subjectSecondaryLang'
      | 'mandatory'
      | 'commentPrimaryLang'
      | 'commentSecondaryLang'
      | 'decisionPrimaryLang'
      | 'decisionSecondaryLang'
    >[];
  };
}) => {
  const matis = orderBy(mat.meetingAgendaTemplateItems, mati => mati.order);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [showEditItemDialog, setShowEditItemDialog] = useState<{
    mati?: Pick<
      GqlMeetingAgendaTemplateItem,
      | 'id'
      | 'order'
      | 'kind'
      | 'subjectPrimaryLang'
      | 'subjectSecondaryLang'
      | 'mandatory'
      | 'commentPrimaryLang'
      | 'commentSecondaryLang'
      | 'decisionPrimaryLang'
      | 'decisionSecondaryLang'
      | 'firstGroup'
      | 'lastGroup'
    >;
    meetingAgendaTemplateId?: string;
  } | null>(null);
  const lastFirstGroupOrder = matis.findLast(ai => ai.firstGroup)!.order + 1;
  const firstLastGroupOrder = matis.find(ai => ai.lastGroup)!.order + 1;
  const availableRange = range(lastFirstGroupOrder, firstLastGroupOrder);
  const movableRange = initial(availableRange);

  const [doDeleteMati, { loading: deleteLoading }] = useMatiDeleteMutation({
    refetchQueries: [BoardMeetingTemplatesDocument],
  });
  const [doMove, { loading: moveLoading }] = useMatiSetOrderMutation({
    refetchQueries: [BoardMeetingTemplatesDocument],
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doSetName] = useMatSetNameMutation({
    refetchQueries: [BoardMeetingTemplatesDocument],
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doSetIsDefault] = useMatSetIsDefaultMutation({
    variables: { companyId: mat.companyId, input: { id: mat.id, isDefault: true } },
    refetchQueries: [BoardMeetingTemplatesDocument],
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doDeleteMat] = useMatDeleteMutation({
    variables: { companyId: mat.companyId, id: mat.id },
    refetchQueries: [BoardMeetingTemplatesDocument],
  });

  const { register, handleSubmit } = useForm<FormValues>({ defaultValues: { name: mat.name } });

  const onSubmit: SubmitHandler<FormValues> = async values => {
    await doSetName({ variables: { companyId: mat.companyId, input: { id: mat.id, name: values.name } } });
    setShowRenameDialog(false);
  };

  const handleEditMati = (id: string) => {
    const found = mat.meetingAgendaTemplateItems.find(mati => mati.id === id);
    if (!found) {
      return;
    }
    setShowEditItemDialog({ mati: found });
  };

  return (
    <>
      <Stack gap={2} marginY={2}>
        <Stack direction="row" gap={1} justifyContent="space-between">
          <Button onClick={() => setShowEditItemDialog({ meetingAgendaTemplateId: mat.id })}>
            <Trans id="Add Agenda Item" />
          </Button>
          <Stack direction="row" gap={1}>
            {!mat.isDefault && (
              <Button variant="outlined" color="neutral" onClick={() => doSetIsDefault()}>
                <Trans id="Set as Default" />
              </Button>
            )}

            <Button variant="outlined" color="neutral" onClick={() => setShowRenameDialog(true)}>
              <Trans id="Rename" />
            </Button>
            <IconButton variant="outlined" color="danger" onClick={() => doDeleteMat()}>
              <DeleteIcon />
            </IconButton>
          </Stack>
        </Stack>
        <AgendaTable
          secondaryLang
          agendaItems={matis}
          movableRange={movableRange}
          doDelete={id => doDeleteMati({ variables: { companyId: mat.companyId, id } })}
          doMove={(id, order) => doMove({ variables: { companyId: mat.companyId, input: { id, order } } })}
          doEdit={handleEditMati}
          loading={deleteLoading || moveLoading}
        />
      </Stack>
      {showRenameDialog && (
        <PromptDialog
          title={<Trans id="Rename Template" />}
          onCancel={() => setShowRenameDialog(false)}
          onSubmit={handleSubmit(onSubmit)}
          loading={false}
        >
          <FormControl>
            <FormLabel required>
              <Trans id="New Name" />
            </FormLabel>
            <Input {...register('name')} required />
          </FormControl>
        </PromptDialog>
      )}
      {showEditItemDialog && (
        <BoardMeetingTemplateItemDialog
          mati={showEditItemDialog.mati}
          meetingAgendaTemplateId={showEditItemDialog.meetingAgendaTemplateId}
          onClose={() => setShowEditItemDialog(null)}
          movableRange={movableRange}
        />
      )}
    </>
  );
};
